import {
    faCamera,
    faMicrophone,
    faPlay,
    faVideo,
    faStop,
    faRotateRight
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as React from 'react';
import styled from 'styled-components';
import { createClient } from '@deepgram/sdk';
import { LiveTranscriptionEvents } from '@deepgram/sdk';

interface VoicePromptProps {
    userText: string;
    setUserText: (text: string) => void;
    isVideo?: boolean;
    isCamera?: boolean;
    isDualCamera?: boolean;
}

const DigitalAvatarContainer = styled.div`
    position: fixed;
    right: 20;
    top: 20;
    z-index: 100;

    .live-player {
        height: 100%;
        width: 100%;
        object-fit: cover;
    }
`;

export function VoicePrompt({
    userText,
    setUserText,
    isVideo,
    isCamera,
    isDualCamera
}: VoicePromptProps) {
    const [hasMicPermission, setHasMicPermission] = React.useState(false);
    const [hasVideoPermission, setHasVideoPermission] = React.useState(false);
    const [stream, setStream] = React.useState(null);
    const [liveStream, setLiveStream] = React.useState<any>(null);
    const mediaRecorder = React.useRef(null);
    const liveVideoFeed = React.useRef<any>(null);
    const [isRecording, setIsRecording] = React.useState(false);
    const [chunks, setChunks] = React.useState([]);
    const [savedMedia, setSavedMedia] = React.useState(null);
    const mimeType = isVideo ? 'video/webm' : 'audio/webm';
    const [isPlaying, setIsPlaying] = React.useState(false);
    const [timer, setTimer] = React.useState(0);
    const [mediaDuration, setMediaDuration] = React.useState(0);
    const imageCanvas = React.useRef(null);
    const [capturedImage, setCapturedImage] = React.useState(null);
    const [defaultWidth, setDefaultWidth] = React.useState(150);
    const [defaultHeight, setDefaultHeight] = React.useState(150);
    const [mediaWidth, setMediaWidth] = React.useState(defaultWidth);
    const [mediaHeight, setMediaHeight] = React.useState(defaultHeight);
    const [cameraDirection, setCameraDirection] = React.useState('environment');
    const [facingModes, setFacingModes] = React.useState([]);
    const [isTranscriptionLoading, setIsTranscriptionLoading] =
        React.useState(false);
    const [transcriptionResultsEvents, setTranscriptionResultsEvents] =
        React.useState([]);
    const [
        processedTranscriptionResultsIdxs,
        setProcessedTranscriptionResultsIdxs
    ] = React.useState([]);

    React.useEffect(() => {
        const newTranscriptionResultsEventIdxTuples = Object.entries(
            transcriptionResultsEvents
        ).filter(
            (idxEventTuple) =>
                !processedTranscriptionResultsIdxs.includes(idxEventTuple[0])
        );
        const newTranscriptionResultsEventIdxs =
            newTranscriptionResultsEventIdxTuples.map((tuple) => tuple[0]);
        const newTranscriptionResultsEvents =
            newTranscriptionResultsEventIdxTuples.map((tuple) => tuple[1]);
        setProcessedTranscriptionResultsIdxs((prevIdxs) => [
            ...prevIdxs,
            ...newTranscriptionResultsEventIdxs
        ]);
        const appendedResponseText = newTranscriptionResultsEvents
            .map((event) => event.channel.alternatives[0].transcript)
            .filter((transcript) => transcript?.length)
            .join('\n');
        if (appendedResponseText.length)
            setUserText(
                userText?.length
                    ? `${userText}\n\n${appendedResponseText}`
                    : appendedResponseText
            );
    }, [transcriptionResultsEvents]);

    const cameraDirections = ['user', 'environment'];

    const permissionsArray = isVideo
        ? ['camera', 'microphone']
        : isCamera || isDualCamera
        ? ['camera']
        : ['microphone'];
    const isReady = isVideo
        ? hasMicPermission && hasVideoPermission
        : isCamera || isDualCamera
        ? hasVideoPermission
        : hasMicPermission;

    const isAudio = !isVideo && !isCamera && !isDualCamera;

    React.useEffect(() => {
        setSavedMedia(null);
        const getPermission = async (permission) => {
            try {
                const result = await navigator.permissions.query({
                    name: permission
                });
                if (permission == 'microphone' && result.state == 'granted') {
                    setHasMicPermission(true);
                }
                if (permission == 'camera' && result.state == 'granted') {
                    setHasVideoPermission(true);
                }
                return true;
            } catch (error) {
                return error;
            }
        };
        setUserText('');
        clearPhoto();
        liveVideoFeed.current.srcObject = null;

        if (isVideo) {
            requestVideoPermission();
        } else if (isCamera) {
            requestCameraPermission();
        } else {
            requestAudioPermission();
        }
        permissionsArray.forEach((item) => getPermission(item));
        return () => {
            // TODO: cleanup async functions
        };
    }, [isVideo, isCamera, isDualCamera, isAudio]);

    const requestAudioPermission = async () => {
        if ('MediaRecorder' in window) {
            try {
                const streamData = await navigator.mediaDevices.getUserMedia({
                    audio: true
                });
                setHasMicPermission(true);
                setStream(streamData);
            } catch (err) {
                alert(
                    'Unfortunately this device does not support media capture. Please try again on another device or update your permission settings.'
                );

                console.log(err.message);
            }
        } else {
            alert('The MediaRecorder is not supported in your browser.');
        }
    };

    const requestCameraPermission = async () => {
        if ('MediaRecorder' in window) {
            try {
                const videoSettings = {
                    video: true,
                    facingMode: cameraDirection
                };

                const supported =
                    await navigator.mediaDevices.getSupportedConstraints();

                // console.log('supported', supported);

                const videoStream = await navigator.mediaDevices.getUserMedia(
                    videoSettings
                );
                const capabilities = videoStream
                    ?.getVideoTracks()[0]
                    ?.getCapabilities();

                if (
                    supported.facingMode &&
                    capabilities?.facingMode &&
                    capabilities?.facingMode.length > 1
                ) {
                    setFacingModes(capabilities?.facingMode);
                }
                console.log('capabilities', capabilities);
                setHasVideoPermission(true);

                const combinedStream = new MediaStream([
                    ...videoStream.getVideoTracks()
                ]);
                setStream(combinedStream);
                setLiveStream(videoStream);
                liveVideoFeed.current.srcObject = videoStream;
                liveVideoFeed.current.play();
            } catch (err) {
                alert(
                    'Unfortunately this device does not support media capture. Please try again on another device or update your permission settings.'
                );

                console.log(err.message);
            }
        } else {
            alert('The MediaRecorder API is not supported in your browser.');
        }
    };

    const requestVideoPermission = async () => {
        if ('MediaRecorder' in window) {
            try {
                const audioSettings = { audio: true };
                const videoSettings = {
                    audio: false,
                    video: true,
                    facingMode: cameraDirection
                };

                const audioStream = await navigator.mediaDevices.getUserMedia(
                    audioSettings
                );
                setHasMicPermission(true);

                const videoStream = await navigator.mediaDevices.getUserMedia(
                    videoSettings
                );
                setHasVideoPermission(true);

                const combinedStream = new MediaStream([
                    ...videoStream.getVideoTracks(),
                    ...audioStream.getAudioTracks()
                ]);
                setStream(combinedStream);
                setLiveStream(videoStream);
            } catch (err) {
                alert(
                    'Unfortunately this device does not support media capture. Please try again on another device or update your permission settings.'
                );

                console.log(err.message);
            }
        } else {
            alert('The MediaRecorder API is not supported in your browser.');
        }
    };

    const clearPhoto = () => {
        setCapturedImage(null);
    };

    const captureImage = () => {
        const context = imageCanvas.current.getContext('2d');

        context.drawImage(liveVideoFeed.current, 0, 0, mediaWidth, mediaHeight);

        const data = imageCanvas.current.toDataURL('image/png');
        setCapturedImage(data);
    };

    const switchCameras = () => {
        setCameraDirection(
            cameraDirections[0] == cameraDirection
                ? cameraDirection[1]
                : cameraDirection[0]
        );

        requestCameraPermission();
    };

    const startRecording = async () => {
        setSavedMedia(null);
        setIsRecording(true);
        setUserText('');

        // TODO: refactor later - repetitive?
        if (isVideo) {
            const videoStream = await navigator.mediaDevices.getUserMedia({
                audio: false,
                video: true
            });
            const audioStream = await navigator.mediaDevices.getUserMedia({
                audio: true
            });

            const combinedStream = new MediaStream([
                ...videoStream.getVideoTracks(),
                ...audioStream.getAudioTracks()
            ]);
            setStream(combinedStream);
            liveVideoFeed.current.srcObject = liveStream;
        }
        if (isAudio) {
            const streamData = await navigator.mediaDevices.getUserMedia({
                audio: true
            });
            setStream(streamData);
        }

        //create new Media recorder instance using the stream
        const media = new MediaRecorder(stream);
        //set the MediaRecorder instance to the mediaRecorder ref
        mediaRecorder.current = media;
        //invokes the start method to start the recording process
        mediaRecorder.current.start();
        const localAudioChunks = [];

        const deepgram = createClient(
            '2a9fc237e2f41980606fcb4a065e781641219328'
        );
        const deepgramLive = deepgram.listen.live({ model: 'nova' });

        deepgramLive.addListener(LiveTranscriptionEvents.Open, () => {
            // console.log('dg open');
            deepgramLive.addListener(
                LiveTranscriptionEvents.Transcript,
                (transcriptionResultsEvent) => {
                    if (
                        transcriptionResultsEvent.channel.alternatives[0]
                            .transcript !== ''
                    ) {
                        setTranscriptionResultsEvents((prevEvents) => [
                            ...prevEvents,
                            transcriptionResultsEvent
                        ]);
                    }
                }
            );

            deepgramLive.addListener(LiveTranscriptionEvents.Close, () => {
                // console.log('dg close');
                setIsTranscriptionLoading(false);
            });

            deepgramLive.addListener(LiveTranscriptionEvents.Error, (error) =>
                console.log({ error })
            );
        });

        mediaRecorder.current.ondataavailable = (event) => {
            if (typeof event.data === 'undefined') return;
            if (event.data.size === 0) return;
            // console.log('liveTranscriptionService.send:', event.data);
            deepgramLive.send(event.data);
            deepgramLive.finish();

            localAudioChunks.push(event.data);
        };
        setChunks(localAudioChunks);
    };

    const stopRecording = () => {
        setIsRecording(false);
        liveVideoFeed.current.srcObject = null;
        //stops the recording instance
        mediaRecorder.current.stop();
        mediaRecorder.current.onstop = () => {
            //creates a blob file from the audiochunks data
            const mediaBlob = new Blob(chunks, { type: mimeType });
            //creates a playable URL from the blob file.
            const blobUrl = URL.createObjectURL(mediaBlob);
            setSavedMedia(blobUrl);
            setChunks([]);
            setMediaDuration(timer);
            setIsTranscriptionLoading(true);
        };
    };

    const handleRecordStart = () => {
        if (isCamera || isDualCamera) {
            captureImage();
        } else if (!isRecording) {
            startRecording();
        } else {
            stopRecording();
        }
    };

    const handlePlay = () => {
        setIsPlaying((prevState) => !prevState);
    };

    React.useEffect(() => {
        let interval;

        if (isRecording || isPlaying) {
            interval = setInterval(() => {
                setTimer((prevTime) => prevTime + 1);
            }, 1000);
        } else {
            clearInterval(interval);
            setTimer(0);
        }

        return () => clearInterval(interval);
    }, [isRecording, isPlaying]);

    const formatTime = (seconds) => {
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = seconds % 60;
        return `${minutes < 10 ? '0' : ''}${minutes}:${
            remainingSeconds < 10 ? '0' : ''
        }${remainingSeconds}`;
    };

    const handleOnProgressBarClick = (e) => {
        const clickedTime = Math.round(
            (e.nativeEvent.offsetX / e.currentTarget.offsetWidth) *
                mediaDuration
        );

        if (mediaRecorder.current) {
            mediaRecorder.current.currentTime = clickedTime;
            setTimer(clickedTime);
        }
    };

    const updateDimensions = () => {
        setMediaHeight(liveVideoFeed.current.videoHeight || defaultHeight);
        setMediaWidth(liveVideoFeed.current.videoWidth || defaultWidth);
    };

    const displayWidth = mediaWidth * (defaultHeight / mediaHeight);

    React.useEffect(() => {
        let resizeTimer;

        const handleResize = () => {
            clearTimeout(resizeTimer);
            resizeTimer = setTimeout(() => {
                const newWidth = window.innerWidth >= 768 ? 150 : 110;
                const newHeight = window.innerWidth >= 768 ? 150 : 110;
                setDefaultWidth(newWidth);
                setDefaultHeight(newHeight);
            }, 200);
        };

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    return (
        <>
            <DigitalAvatarContainer
                style={{
                    width: displayWidth,
                    height: defaultHeight
                }}
            >
                <video
                    ref={liveVideoFeed}
                    autoPlay
                    className="live-player"
                    onLoadedMetadata={updateDimensions}
                    playsInline
                />
            </DigitalAvatarContainer>
            {isPlaying && isVideo ? (
                <DigitalAvatarContainer
                    style={{
                        width: displayWidth,
                        height: defaultHeight
                    }}
                >
                    <video
                        className="live-player"
                        autoPlay
                        onEnded={handlePlay}
                        ref={mediaRecorder}
                        playsInline
                    >
                        <source src={savedMedia} type="video/mp4" />
                    </video>
                </DigitalAvatarContainer>
            ) : null}

            {(isCamera || isDualCamera) && capturedImage && (
                <DigitalAvatarContainer
                    style={{
                        width: displayWidth,
                        height: defaultHeight
                    }}
                >
                    <img className="live-player" src={capturedImage} />
                </DigitalAvatarContainer>
            )}
            <canvas
                id="canvas"
                ref={imageCanvas}
                width={mediaWidth}
                height={mediaHeight}
                style={{ display: 'none' }}
            />
            {isReady ? (
                <div className="record-wrap">
                    <div className="icons">
                        <div
                            className={`icons record-icon ${
                                isPlaying ? 'disabled' : ''
                            }`}
                            onClick={!isPlaying ? handleRecordStart : undefined}
                        >
                            {isRecording ? (
                                <span>{formatTime(timer)}</span>
                            ) : (
                                <FontAwesomeIcon
                                    icon={
                                        isVideo
                                            ? faVideo
                                            : isCamera || isDualCamera
                                            ? faCamera
                                            : faMicrophone
                                    }
                                />
                            )}
                        </div>
                        {savedMedia && (
                            <>
                                <div
                                    className={`icons record-icon ${
                                        isRecording ? 'disabled' : ''
                                    }`}
                                    onClick={
                                        !isRecording ? handlePlay : undefined
                                    }
                                >
                                    {isPlaying ? (
                                        <FontAwesomeIcon icon={faStop} />
                                    ) : (
                                        <FontAwesomeIcon icon={faPlay} />
                                    )}
                                </div>
                                <div
                                    style={{
                                        width: defaultWidth,
                                        background: 'none'
                                    }}
                                >
                                    {isPlaying && !isVideo && (
                                        <audio
                                            autoPlay
                                            onEnded={handlePlay}
                                            ref={mediaRecorder}
                                            src={savedMedia}
                                        />
                                    )}
                                    <progress
                                        id="progress"
                                        value={timer}
                                        max={mediaDuration}
                                        ref={mediaRecorder}
                                        style={{
                                            width: '100%',
                                            cursor: 'pointer'
                                        }}
                                        onClick={handleOnProgressBarClick}
                                    />
                                </div>
                                <p>{formatTime(timer)}</p>
                            </>
                        )}
                        {isCamera && facingModes.length > 1 && (
                            <div
                                className={`icons record-icon ${
                                    isPlaying ? 'disabled' : ''
                                }`}
                                onClick={switchCameras}
                            >
                                <FontAwesomeIcon icon={faRotateRight} />
                            </div>
                        )}
                    </div>
                    {/* {isRecording
                    ? getTranslation(t, 'PLACEHOLDER.PROMPT.RECORD_STOP')
                : getTranslation(t, 'PLACEHOLDER.PROMPT.RECORD_START')} */}
                    {!savedMedia ? (
                        <span className="helper-text">
                            {isCamera || isDualCamera
                                ? 'Tap camera icon to take a photo'
                                : isRecording
                                ? 'Tap to stop recording'
                                : 'Tap to start recording'}
                        </span>
                    ) : (
                        <span
                            className={`helper-text ${
                                isTranscriptionLoading ? 'loading' : 'danger'
                            }`}
                        >
                            {isTranscriptionLoading
                                ? 'Please wait while audio processes before sending.'
                                : userText === '' &&
                                  'Sorry, there was an error with recording! Please try again.'}
                        </span>
                    )}
                </div>
            ) : (
                <div className="permission-request">
                    <div>Please give recording permissions to continue</div>
                    <button
                        onClick={(e) => {
                            e.preventDefault();
                            if (isVideo) {
                                requestVideoPermission();
                            } else {
                                requestAudioPermission();
                            }
                        }}
                    >
                        Request Permission
                    </button>
                </div>
            )}
        </>
    );
}

export default VoicePrompt;
